import { CommonModule } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatChipsModule } from '@angular/material/chips'
import { MatDialog } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { ProgressLoaderComponent } from '../../common/progress-loader/progress-loader.component'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import { ActivatedRoute, Router } from '@angular/router'
import { HocuspocusProvider } from '@hocuspocus/provider'
import { injectQueryClient } from '@ngneat/query'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { AnyExtension, ChainedCommands, Editor, isTextSelection } from '@tiptap/core'
import CharacterCount from '@tiptap/extension-character-count'
import Collaboration from '@tiptap/extension-collaboration'
import CollaborationCaret from '@tiptap/extension-collaboration-caret'
import FindAndReplace from '@tiptap/extension-find-and-replace'
import { Mark as ProseMirrorMark } from '@tiptap/pm/model'
import { EditorState, PluginKey, TextSelection, Transaction } from '@tiptap/pm/state'
import { EditorView } from '@tiptap/pm/view'
import { TiptapBubbleMenuDirective, TiptapEditorDirective } from 'ngx-tiptap'
import { BehaviorSubject, combineLatest, filter, lastValueFrom, map, Observable, Subscription, switchMap, take } from 'rxjs'

import * as Y from 'yjs'
import { swapListType } from '../../../../../backend/src/tiptap/editor/extensions/nodes/list'
import {
  COLOR_ATTRIBUTE_NAME,
  ColorChange,
  FormattingChanges,
  LIST_ITEM_NODE_NAME,
  ListChange,
  SUGGESTION_MARK_NAME,
  SUGGESTION_TYPE_DELETION,
  SUGGESTION_TYPE_FORMATTING,
  SUGGESTION_TYPE_INSERTION,
  TEXT_STYLE_MARK_NAME,
} from '../../../../../backend/src/tiptap/editor/extensions/marks/suggestion'
import { COMMENT_ANCHOR_MARK_NAME } from '../../../../../backend/src/tiptap/editor/extensions/marks/comment-anchor'
import { EDITOR_STRUCTURAL_EXTENSIONS } from '../../../../../backend/src/tiptap/editor-extensions'
import { Call, isMinutesGenerationTargeted, MetricType } from '../../calls.service'
import { LoggerService } from '../../logger.service'
import { MetricsService } from '../../metrics.service'
import { queryKeys } from '../../query-keys'
import { ToastService } from '../../toast.service'
import { MeUser, UsersService } from '../../users.service'
import { constructNameForUser, stringArraysEqual } from '../../utils'
import { AiActionFactCheckDialogComponent, AiActionFactCheckDialogData } from './ai-actions/ai-action-fact-check-dialog.component'
import { AiActionStreamingDialogComponent, AiActionStreamingDialogData } from './ai-actions/ai-action-streaming-dialog.component'
import { AI_ACTION_CONFIGS, LlmAction, SentenceIdPair } from './ai-actions/ai-action.types'
import {
  expandSelectionToWordBoundaries,
  getExistingItemContent,
  getSelectedText,
  getSentenceIdPairsFromSelection,
  getTemplateItemIdsFromSelection,
} from './ai-actions/selection-utils'
import { EDITOR_MAXIMIZED_QUERY_PARAM, EditorSettingsService } from '../editor/editor-settings.service'
import { AgendaMissingWarningComponent } from '../agenda-missing-warning/agenda-missing-warning.component'
import { anchorKey, EditorThread, EditorThreadAnchorType, SidebarFilter } from './editor-thread.types'
import { EditorThreadsService } from './editor-threads.service'
import { CitationExtension } from './extensions/citation-extension'
import { runReplaceAllWithTracking, runReplaceWithTracking } from './extensions/find-replace-track-changes'
import { LockedItemsExtension, refreshLockedItems } from './extensions/locked-items-extension'
import { coversLockedRange, findLockedRanges, haveSameLockedItems, overlapsLockedRange } from './extensions/locked-items-plugin'
import { citationPluginKey } from './extensions/citation-plugin'
import { runColorCommandWithTracking } from './extensions/color-track-changes'
import {
  expandDeletionToEmptiedListItem,
  liftListItemSingleLevel,
  listChangeArraysEqual,
  restoreListItemAtSource,
  runListCommandWithTracking,
} from './extensions/list-track-changes'
import { TrackChangesExtension } from './extensions/track-changes-extension'
import { TRACK_CHANGES_SKIP_META, trackChangesPluginKey } from './extensions/track-changes-plugin'
import {
  collectAllSuggestions,
  collectSuggestionContent,
  collectSuggestionStartPositions,
  colorChangesEqual,
  findAllRangesForSuggestion,
  findSuggestionAtPosition,
  suggestionRunsEqual,
  SuggestionSummary,
  suggestionSummariesEqual,
  SuggestionTextRun,
} from './extensions/track-changes-utils'
import {
  collectCommentAnchors,
  collectCommentAnchorStartPositions,
  collectCommentAnchorSummaries,
  CommentAnchorSummary,
  commentAnchorSummariesEqual,
  findCommentAnchorIdAtPosition,
} from './extensions/comment-anchor-utils'
import { deriveEditorDisplayState, EditorDisplayState } from './editor-display-state'
import { readTrackChangesEnabled, writeTrackChangesEnabled } from './track-changes-storage'
import { SseSyncProvider } from './sse-sync-provider'
import { CommentPopupComponent } from './comment-popup/comment-popup.component'
import { FindReplacePanelComponent } from './find-replace-panel/find-replace-panel.component'
import { SuggestionPopupComponent } from './suggestion-popup/suggestion-popup.component'
import { TiptapEditorSidebarComponent } from './tiptap-editor-sidebar/tiptap-editor-sidebar.component'
import { centerViewOnPosition } from './utils/center-view-on-position'
import { hslToHex } from './utils/hsl-to-hex'
import { TEXT_COLOR_OPTIONS } from './utils/text-colors'

type ActiveSuggestion = {
  suggestionId: string
  authorName: string
  createdAt: string | null
  insertionRuns: SuggestionTextRun[]
  deletionRuns: SuggestionTextRun[]
  formattingAdded: string[]
  formattingRemoved: string[]
  listChanges: ListChange[]
  colorChange: ColorChange | null
}

function navigationIncludes(filter: SidebarFilter): { changes: boolean; comments: boolean } {
  return { changes: filter !== 'comments', comments: filter !== 'changes' }
}

const STATELESS_REFETCH_CALL_MESSAGE_TYPE = 'refetch-call'
const STATELESS_THREAD_UPDATE_PREFIX = 'thread-update:'

const SUGGESTION_POPUP_EDGE_PADDING = 8
// Mirrors the `max-height` cap in suggestion-popup.component.scss so the inline
// style only ever tightens the height — never overrides the SCSS cap on tall editors.
const SUGGESTION_POPUP_MAX_HEIGHT_CAP = 'min(30vh, 210px)'

type SuggestionPopupOptions = {
  strategy: 'fixed' | 'absolute'
  scrollTarget?: HTMLElement
  flip?: boolean
  shift?: { padding?: number }
  size?: { padding?: number; apply?: (args: { availableHeight: number; elements: { floating: HTMLElement } }) => void }
}

function buildSuggestionPopupOptions(getScrollTarget?: () => HTMLElement | undefined): SuggestionPopupOptions {
  return {
    strategy: 'absolute',
    get scrollTarget() {
      return getScrollTarget?.()
    },
    flip: true,
    shift: { padding: SUGGESTION_POPUP_EDGE_PADDING },
    size: {
      padding: SUGGESTION_POPUP_EDGE_PADDING,
      apply: ({ availableHeight, elements }) => {
        const next = `min(${Math.max(0, availableHeight - SUGGESTION_POPUP_EDGE_PADDING)}px, ${SUGGESTION_POPUP_MAX_HEIGHT_CAP})`
        // `apply` runs on every transaction and on every (debounced) scroll. Skipping
        // identical writes avoids a per-keystroke style invalidation on the popover.
        if (elements.floating.style.maxHeight !== next) {
          elements.floating.style.maxHeight = next
        }
      },
    },
  }
}

function findScrollableAncestor(element: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = element
  while (current !== null) {
    const overflowY = window.getComputedStyle(current).overflowY
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
      return current
    }
    current = current.parentElement
  }
  return null
}

@Component({
  selector: 'app-tiptap-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    MatMenuModule,
    ProgressLoaderComponent,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslatePipe,
    TiptapEditorDirective,
    TiptapBubbleMenuDirective,
    AgendaMissingWarningComponent,
    SuggestionPopupComponent,
    CommentPopupComponent,
    FindReplacePanelComponent,
    TiptapEditorSidebarComponent,
  ],
  templateUrl: './tiptap-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './tiptap-editor.component.scss',
})
export class TiptapEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true })
  call!: Call

  @Input({ required: true })
  canUserEdit!: boolean

  @Input()
  canUserComment = false

  @Output()
  openDocument = new EventEmitter<{ resourceId: number; resourcePageNumber: number }>()

  @Output()
  openMeeting = new EventEmitter<void>()

  @Output()
  openAgendaUpload = new EventEmitter<void>()

  usersService = inject(UsersService)
  private http = inject(HttpClient)
  private dialog = inject(MatDialog)
  private toastService = inject(ToastService)
  private metricsService = inject(MetricsService)
  private translateService = inject(TranslateService)
  private editorSettingsService = inject(EditorSettingsService)
  private logger = inject(LoggerService)
  private changeDetectorRef = inject(ChangeDetectorRef)
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private queryClient = injectQueryClient()
  private destroyRef = inject(DestroyRef)
  private editorThreadsService = inject(EditorThreadsService)

  document: Y.Doc | null = null

  hocuspocus: HocuspocusProvider | null = null

  editor: Editor | null = null
  hasInitiallySynced = false
  showWordCount$ = this.editorSettingsService.showWordCount$
  isEditorMaximized$ = this.editorSettingsService.isEditorMaximized$
  zoomLevel$ = this.editorSettingsService.zoomLevel$

  // The bubble menu is only rendered after the document has finished syncing, so this
  // ViewChild resolves later than `ngAfterViewInit` would fire. Using a setter lets us
  // attach the scroll-driven hide handler the first time Angular gives us a real ref.
  @ViewChild(TiptapBubbleMenuDirective, { read: ElementRef })
  set bubbleMenuElementRef(ref: ElementRef<HTMLElement> | undefined) {
    const element = ref?.nativeElement ?? null
    if (element === null || this.editor === null || this.bubbleMenuScrollHandler !== null) {
      return
    }
    this.attachBubbleMenuScrollHandler(this.editor, element)
  }

  @ViewChild(FindReplacePanelComponent)
  findReplacePanel?: FindReplacePanelComponent

  aiActionsConfig = AI_ACTION_CONFIGS
  readonly textColorOptions = TEXT_COLOR_OPTIONS
  currentTextColor = TEXT_COLOR_OPTIONS[0].value
  bubbleMenuOptions: { strategy: 'fixed' | 'absolute'; scrollTarget?: HTMLElement } = { strategy: 'absolute' }
  suggestionPopupOptions: SuggestionPopupOptions = buildSuggestionPopupOptions()

  trackChangesEnabled$ = new BehaviorSubject<boolean>(false)
  trackChangesToggleLabel$ = this.trackChangesEnabled$.pipe(
    map((enabled) => this.translateService.instant(enabled ? 'EDITOR.TRACK_CHANGES.DISABLE_TOOLTIP' : 'EDITOR.TRACK_CHANGES.ENABLE_TOOLTIP'))
  )
  activeSuggestion$ = new BehaviorSubject<ActiveSuggestion | null>(null)
  activeSuggestionId$ = this.activeSuggestion$.pipe(map((suggestion) => suggestion?.suggestionId ?? null))
  suggestionCount$ = new BehaviorSubject<number>(0)
  suggestions$ = new BehaviorSubject<SuggestionSummary[]>([])
  commentAnchors$ = new BehaviorSubject<CommentAnchorSummary[]>([])
  activeCommentAnchorId$ = new BehaviorSubject<string | null>(null)
  activeCommentDraftId$ = new BehaviorSubject<string | null>(null)
  sidebarFilter$ = new BehaviorSubject<SidebarFilter>('all')
  threads$ = this.editorThreadsService.threads$
  hasSidebarContent$ = combineLatest([this.suggestions$, this.threads$, this.commentAnchors$]).pipe(
    map(([suggestions, threads, commentAnchors]) => suggestions.length > 0 || threads.size > 0 || commentAnchors.length > 0)
  )
  hasComments$ = this.commentAnchors$.pipe(map((anchors) => anchors.length > 0))
  showTrackChangesToolbar$ = combineLatest([this.trackChangesEnabled$, this.suggestionCount$, this.hasComments$]).pipe(
    map(([enabled, suggestionCount, hasComments]) => enabled || suggestionCount > 0 || hasComments)
  )
  navigationTargetCount$ = combineLatest([this.suggestionCount$, this.commentAnchors$, this.sidebarFilter$]).pipe(
    map(([suggestionCount, comments, filter]) => {
      const includes = navigationIncludes(filter)
      return (includes.changes ? suggestionCount : 0) + (includes.comments ? comments.length : 0)
    })
  )
  findReplaceOpen$ = new BehaviorSubject<boolean>(false)
  activeCommentPopup$ = new BehaviorSubject<string | null>(null)
  activeCommentPopupThread$: Observable<EditorThread | null> = combineLatest([this.activeCommentPopup$, this.threads$]).pipe(
    map(([anchorId, threads]) => (anchorId === null ? null : (threads.get(anchorKey('TEXT_RANGE', anchorId)) ?? null)))
  )

  readonly suggestionPopupPluginKey = new PluginKey('suggestionPopup')
  readonly commentPopupPluginKey = new PluginKey('commentPopup')

  private citationsSubscription: Subscription | null = null
  private templateItemSubscription: Subscription | null = null
  private synced$ = new BehaviorSubject<boolean>(false)
  private bubbleMenuScrollHandler: (() => void) | null = null
  private bubbleMenuScrollDebounceTimer: number | null = null
  private bubbleMenuScrollElement: HTMLElement | null = null
  private cachedScrollElement: HTMLElement | null = null
  private suggestionRepositionRafId: number | null = null
  private commentRepositionRafId: number | null = null
  private openCommentsRafIds: [number | null, number | null] = [null, null]

  shouldShowFormattingMenu = (props: { element: HTMLElement; view: EditorView; state: EditorState; from: number; to: number }): boolean => {
    if (this.editor === null || (!this.canUserEdit && !this.canUserComment)) {
      return false
    }
    const { doc, selection } = props.state
    if (selection.empty) {
      return false
    }
    const isEmptyTextBlock = isTextSelection(selection) && doc.textBetween(props.from, props.to).length === 0
    if (isEmptyTextBlock) {
      return false
    }
    // Neither an AI action nor a comment is worth offering on content a regeneration is about to replace.
    if (overlapsLockedRange(props.state, props.from, props.to)) {
      return false
    }
    // A commenter's editor is `contenteditable=false`, so `view.hasFocus()` is never
    // true — the DOM element isn't focusable. Fall back to checking the browser
    // selection lives inside the editor so the menu still appears on a plain selection.
    const isChildOfMenu = props.element.contains(document.activeElement)
    return isChildOfMenu || props.view.hasFocus() || this.selectionIsInsideEditor(props.view)
  }

  private selectionIsInsideEditor(view: EditorView): boolean {
    const domSelection = view.dom.ownerDocument.getSelection()
    if (domSelection === null || domSelection.rangeCount === 0) {
      return false
    }
    const { anchorNode, focusNode } = domSelection
    return anchorNode !== null && focusNode !== null && view.dom.contains(anchorNode) && view.dom.contains(focusNode)
  }

  shouldShowSuggestionPopup = (props: { state: EditorState; oldState?: EditorState; from: number; to: number }): boolean => {
    if (this.editor === null) {
      this.refreshActiveSuggestion(null)
      return false
    }

    if (props.from !== props.to) {
      this.refreshActiveSuggestion(null)
      return false
    }

    // Suppress the popover while the document is changing — e.g. the user is
    // typing a tracked change. The popover should only appear when the cursor
    // is manually navigated onto an existing suggestion.
    const oldState = props.oldState ?? null
    if (oldState !== null && !oldState.doc.eq(props.state.doc)) {
      this.refreshActiveSuggestion(null)
      return false
    }

    const suggestion = findSuggestionAtPosition(props.state, props.from)
    if (suggestion === null) {
      this.refreshActiveSuggestion(null)
      return false
    }

    const fallbackAuthor = this.translateService.instant('EDITOR.TRACK_CHANGES.UNKNOWN_AUTHOR')
    const collected = collectSuggestionContent(props.state, suggestion.suggestionId)

    this.refreshActiveSuggestion({
      suggestionId: suggestion.suggestionId,
      authorName: suggestion.authorName ?? fallbackAuthor,
      createdAt: suggestion.createdAt,
      insertionRuns: collected.insertionRuns,
      deletionRuns: collected.deletionRuns,
      formattingAdded: collected.formattingAdded,
      formattingRemoved: collected.formattingRemoved,
      listChanges: collected.listChanges,
      colorChange: collected.colorChange,
    })
    // In maximized mode the sidebar replaces the popover, but the active-suggestion
    // state still drives the scroll-and-highlight effect on the matching card.
    if (this.editorSettingsService.isEditorMaximized) {
      return false
    }
    return true
  }

  shouldShowCommentPopup = (props: { state: EditorState; oldState?: EditorState; from: number; to: number }): boolean => {
    const anchorId = this.findCommentAnchorAtCursor(props)
    this.refreshActiveCommentAnchor(anchorId)

    if (this.editor === null || !this.canUserComment || this.editorSettingsService.isEditorMaximized) {
      this.activeCommentPopup$.next(null)
      return false
    }
    if (anchorId === null) {
      this.activeCommentPopup$.next(null)
      return false
    }
    if (this.activeCommentPopup$.value !== anchorId) {
      this.activeCommentPopup$.next(anchorId)
    }
    return true
  }

  ngOnInit() {
    const user = this.usersService.currentUser$.value
    const name = user !== null ? constructNameForUser(user) : 'Unknown User'

    this.trackChangesEnabled$.next(readTrackChangesEnabled(this.call.id))

    this.document = new Y.Doc()

    this.hocuspocus = new SseSyncProvider({
      name: `minutes:${this.call.id}`,
      document: this.document,
      forcePolling: user?.tiptapEditorPollingFallbackActivatedAt != null,
      onSynced: ({ state }) => {
        if (!this.hasInitiallySynced && state) {
          this.hasInitiallySynced = true
          this.synced$.next(true)
          this.changeDetectorRef.detectChanges()
        }
      },
      onStatelessPayload: (payload) => {
        if (payload === STATELESS_REFETCH_CALL_MESSAGE_TYPE) {
          this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(this.call.id) })
        } else if (payload.startsWith(STATELESS_THREAD_UPDATE_PREFIX)) {
          this.editorThreadsService.requestRefetch(this.call.id)
        }
      },
    })

    this.editorThreadsService.loadAll(this.call.id)

    const extensions: AnyExtension[] = [
      ...EDITOR_STRUCTURAL_EXTENSIONS,
      CharacterCount,
      Collaboration.configure({
        document: this.document,
      }),
      CollaborationCaret.configure({
        provider: this.hocuspocus,
        user: {
          name,
          color: hslToHex(Math.floor(Math.random() * 360), 0.75, 0.8),
        },
      }),
      TrackChangesExtension.configure(this.trackChangesCommandOptions()),
      FindAndReplace.configure({ injectCSS: false }),
    ]

    // Installed unconditionally: citations can arrive on a later call refresh, and only an
    // installed plugin can turn them into decorations when `syncCitations` asks for a rebuild.
    extensions.push(
      CitationExtension.configure({
        // Read lazily, so a call refresh that carries no citations reaches the plugin as null.
        getCitations: () => this.call.minutesCitations,
        getResources: () => this.call.userUploadedResources.map((resource) => ({ id: resource.id, fileName: resource.fileName })),
        formatPageLabel: (page: number) => this.translateService.instant('EDITOR.CITATIONS.PAGE_NUMBER', { pageNumber: page }),
        onOpenDocument: (event) => this.openDocument.emit(event),
        onOpenMeeting: (event) => this.handleOpenMeeting(event),
      })
    )

    // Also installed unconditionally: a regeneration can start while the editor is already open, and the locked
    // set is read lazily so the plugin sees each call refresh.
    extensions.push(LockedItemsExtension.configure({ getLockedTemplateItemIds: () => this.regeneratingTemplateItemIds }))

    this.editor = new Editor({
      extensions,
      editorProps: {
        handleScrollToSelection: (view) => this.smoothScrollToSelection(view),
      },
    })
    this.editor.setEditable(this.canUserEdit)
    if (!this.canUserComment) {
      this.editor.view.dom.classList.add('comments-hidden')
    }
    const getScrollElement = () => this.resolveScrollElement()
    this.bubbleMenuOptions = {
      strategy: 'absolute',
      get scrollTarget() {
        return getScrollElement()
      },
    }
    this.suggestionPopupOptions = buildSuggestionPopupOptions(getScrollElement)

    this.editor.on('update', () => {
      this.refreshSuggestionCount()
      this.refreshSuggestions()
      this.refreshCommentAnchors()
    })
    this.refreshCommentAnchors()
    this.trackChangesEnabled$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.refreshSuggestionCount()
    })
    this.isEditorMaximized$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.refreshSuggestions()
      this.refreshCommentAnchors()
    })

    // The bubble menu computes its position before Angular renders the popover's
    // content, so the element measures at zero width and `shift` sees no overflow.
    // Re-trigger an update once the popover content has been laid out.
    this.activeSuggestion$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((suggestion) => {
      if (suggestion === null) {
        return
      }
      this.suggestionRepositionRafId = this.repositionPopupAfterLayout(this.suggestionPopupPluginKey, this.suggestionRepositionRafId)
    })
    this.activeCommentPopup$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((anchorId) => {
      if (anchorId === null) {
        return
      }
      this.commentRepositionRafId = this.repositionPopupAfterLayout(this.commentPopupPluginKey, this.commentRepositionRafId)
    })

    // Also unconditional, so citations that arrive later still honour the show-citations setting.
    this.citationsSubscription = this.editorSettingsService.showCitations$.subscribe((showCitations) => {
      const dom = this.editor?.view.dom ?? null

      if (dom === null) {
        return
      }

      if (showCitations) {
        dom.classList.remove('citations-hidden')
      } else {
        dom.classList.add('citations-hidden')
      }
    })

    this.templateItemSubscription = this.route.queryParams
      .pipe(
        map((params) => params['templateItem'] ?? null),
        filter((templateItemId) => templateItemId !== null),
        switchMap((templateItemId) =>
          this.synced$.pipe(
            filter((synced) => synced),
            take(1),
            map(() => templateItemId)
          )
        )
      )
      .subscribe((templateItemId) => {
        this.scrollToTemplateItem(Number(templateItemId))
        this.router.navigate([], {
          queryParams: { templateItem: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        })
      })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.editor === null) {
      return
    }
    this.syncEditableState(changes)
    this.syncCommentState(changes)
    this.syncCitations(changes)
    this.syncLockedItems(changes)
  }

  private syncLockedItems(changes: SimpleChanges) {
    const change = changes['call']
    if (this.editor === null || change === undefined || change.firstChange) {
      return
    }
    // Every refetch brings a fresh array, so the ids are what says whether the lock set moved.
    const previousCall = change.previousValue as Call
    const previouslyRegeneratingTemplateItemIds = previousCall.minutesGenerationTargetTemplateItemIds ?? []
    if (haveSameLockedItems(previouslyRegeneratingTemplateItemIds, this.regeneratingTemplateItemIds)) {
      return
    }
    refreshLockedItems(this.editor)
    this.scrollToFirstRegeneratingItem(previouslyRegeneratingTemplateItemIds)
  }

  /**
   * Follows a starting regeneration to the item it has just taken over, so the user watches the part of the
   * minutes that is being rewritten rather than wherever they happened to be scrolled to.
   *
   * Only items the lock set has gained count as starting: one that was already being rewritten before this
   * change should not pull the viewport back to itself.
   */
  private scrollToFirstRegeneratingItem(previouslyRegeneratingTemplateItemIds: number[]) {
    if (this.editor === null) {
      return
    }

    const alreadyRegeneratingTemplateItemIds = new Set(previouslyRegeneratingTemplateItemIds)
    const startedTemplateItemIds = this.regeneratingTemplateItemIds.filter((templateItemId) => !alreadyRegeneratingTemplateItemIds.has(templateItemId))
    const [firstStartedRange] = findLockedRanges(this.editor.state.doc, startedTemplateItemIds)
    if (firstStartedRange === undefined) {
      return
    }

    const templateItemId = this.editor.state.doc.nodeAt(firstStartedRange.from)?.attrs['templateItemId']
    if (typeof templateItemId !== 'string') {
      return
    }

    this.scrollToTemplateItem(Number(templateItemId))
  }

  private syncEditableState(changes: SimpleChanges) {
    const change = changes['canUserEdit']
    if (this.editor === null || change === undefined || change.firstChange) {
      return
    }
    this.editor.setEditable(this.canUserEdit)
  }

  private syncCommentState(changes: SimpleChanges) {
    const change = changes['canUserComment']
    if (this.editor === null || change === undefined || change.firstChange) {
      return
    }
    this.editor.view.dom.classList.toggle('comments-hidden', !this.canUserComment)
    if (!this.canUserComment) {
      this.activeCommentDraftId$.next(null)
      this.activeCommentPopup$.next(null)
    }
    this.refreshCommentAnchors()
  }

  private syncCitations(changes: SimpleChanges) {
    const change = changes['call']
    if (this.editor === null || change === undefined || change.firstChange) {
      return
    }
    const previousCall = change.previousValue as Call
    if (previousCall.minutesCitations !== this.call.minutesCitations) {
      this.editor.view.dispatch(this.editor.state.tr.setMeta(citationPluginKey, { type: 'REBUILD' }))
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleZoomShortcut(event: KeyboardEvent) {
    if (!(event.metaKey || event.ctrlKey) || event.altKey) {
      return
    }
    if (!this.editorSettingsService.isEditorMaximized || this.editor === null || !this.editor.view.hasFocus()) {
      return
    }
    switch (event.key) {
      case '=':
      case '+':
        event.preventDefault()
        this.editorSettingsService.zoomIn()
        break
      case '-':
      case '_':
        event.preventDefault()
        this.editorSettingsService.zoomOut()
        break
      case '0':
        event.preventDefault()
        this.editorSettingsService.resetZoom()
        break
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleFindReplaceShortcut(event: KeyboardEvent) {
    if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey || event.key.toLowerCase() !== 'f') {
      return
    }
    if (this.editor === null || (!this.editor.view.hasFocus() && !this.findReplaceOpen$.value)) {
      return
    }
    event.preventDefault()
    if (this.findReplaceOpen$.value) {
      this.findReplacePanel?.focusSearchInput()
      return
    }
    this.findReplaceOpen$.next(true)
  }

  private attachBubbleMenuScrollHandler(editor: Editor, bubbleMenuElement: HTMLElement) {
    this.bubbleMenuScrollHandler = () => {
      if (!editor.state.selection.empty) {
        return
      }

      if (this.bubbleMenuScrollDebounceTimer !== null) {
        clearTimeout(this.bubbleMenuScrollDebounceTimer)
      }

      // Must exceed the bubble menu plugin's internal resizeDelay (60ms) so our
      // visibility correction runs after the plugin's scroll-triggered updatePosition().
      this.bubbleMenuScrollDebounceTimer = window.setTimeout(() => {
        if (editor.state.selection.empty) {
          bubbleMenuElement.style.visibility = 'hidden'
        }
      }, 70)
    }

    this.bubbleMenuScrollElement = this.resolveScrollElement() ?? (editor.view.dom as HTMLElement)
    this.bubbleMenuScrollElement.addEventListener('scroll', this.bubbleMenuScrollHandler, { passive: true })
  }

  private resolveScrollElement(): HTMLElement | undefined {
    if (this.cachedScrollElement?.isConnected) {
      return this.cachedScrollElement
    }
    const dom = this.editor?.view.dom as HTMLElement | undefined
    this.cachedScrollElement = (dom?.closest('.editor') as HTMLElement | null) ?? null
    return this.cachedScrollElement ?? dom
  }

  private repositionPopupAfterLayout(pluginKey: PluginKey, previousRafId: number | null): number {
    if (previousRafId !== null) {
      cancelAnimationFrame(previousRafId)
    }
    return requestAnimationFrame(() => {
      if (this.editor === null) {
        return
      }
      this.editor.view.dispatch(this.editor.state.tr.setMeta(pluginKey, 'updatePosition'))
    })
  }

  ngOnDestroy() {
    if (this.bubbleMenuScrollDebounceTimer !== null) {
      clearTimeout(this.bubbleMenuScrollDebounceTimer)
    }
    if (this.suggestionRepositionRafId !== null) {
      cancelAnimationFrame(this.suggestionRepositionRafId)
      this.suggestionRepositionRafId = null
    }
    if (this.commentRepositionRafId !== null) {
      cancelAnimationFrame(this.commentRepositionRafId)
      this.commentRepositionRafId = null
    }
    this.cancelOpenCommentsRafs()
    if (this.bubbleMenuScrollHandler !== null && this.bubbleMenuScrollElement !== null) {
      this.bubbleMenuScrollElement.removeEventListener('scroll', this.bubbleMenuScrollHandler)
    }
    this.citationsSubscription?.unsubscribe()
    this.templateItemSubscription?.unsubscribe()
    this.synced$.complete()
    this.findReplaceOpen$.complete()
    this.trackChangesEnabled$.complete()
    this.activeSuggestion$.complete()
    this.suggestionCount$.complete()
    this.suggestions$.complete()
    this.commentAnchors$.complete()
    this.activeCommentAnchorId$.complete()
    this.activeCommentDraftId$.complete()
    this.sidebarFilter$.complete()
    this.activeCommentPopup$.complete()
    this.editor?.destroy()
    this.hocuspocus?.destroy()
    this.document?.destroy()
    this.editorThreadsService.reset()
  }

  toggleFindReplace() {
    this.findReplaceOpen$.next(!this.findReplaceOpen$.value)
  }

  closeFindReplace() {
    this.findReplaceOpen$.next(false)
  }

  replaceCurrentMatch() {
    if (this.editor === null || !this.canUserEdit) {
      return
    }
    runReplaceWithTracking(this.editor, this.trackChangesCommandOptions())
  }

  replaceAllMatches() {
    if (this.editor === null || !this.canUserEdit) {
      return
    }
    runReplaceAllWithTracking(this.editor, this.trackChangesCommandOptions())
  }

  toggleTrackChanges() {
    const nextValue = !this.trackChangesEnabled$.value
    this.trackChangesEnabled$.next(nextValue)
    writeTrackChangesEnabled(this.call.id, nextValue)
  }

  acceptSuggestion(suggestionId: string) {
    this.resolveSuggestion(suggestionId, 'accept')
  }

  rejectSuggestion(suggestionId: string) {
    this.resolveSuggestion(suggestionId, 'reject')
  }

  private cachedThreadCurrentUserSource: MeUser | null = null
  private cachedThreadCurrentUser: { name: string } = { name: 'Unknown User' }

  get threadCurrentUser(): { name: string } {
    const user = this.usersService.currentUser$.value
    if (this.cachedThreadCurrentUserSource !== user) {
      this.cachedThreadCurrentUserSource = user
      this.cachedThreadCurrentUser = { name: user !== null ? constructNameForUser(user) : 'Unknown User' }
    }
    return this.cachedThreadCurrentUser
  }

  get canPostComment(): boolean {
    return this.canUserComment && this.usersService.currentUser$.value !== null
  }

  get displayState(): EditorDisplayState {
    return deriveEditorDisplayState({
      isPostProcessingComplete: this.call.isPostProcessingComplete,
      isMinutesGenerationOngoing: this.call.isMinutesGenerationOngoing,
      isMinutesGenerationTargeted: isMinutesGenerationTargeted(this.call),
      minutesGenerationProgress: this.call.minutesGenerationProgress,
      editorConstructed: this.editor !== null,
      hasInitiallySynced: this.hasInitiallySynced,
    })
  }

  /** The agenda items a regeneration is currently rewriting, which are locked while it runs. */
  get regeneratingTemplateItemIds(): number[] {
    return this.call.minutesGenerationTargetTemplateItemIds ?? []
  }

  onCardSubmitComment(suggestionId: string, body: string) {
    this.postThreadComment('SUGGESTION', suggestionId, body)
  }

  private postThreadComment(anchorType: EditorThreadAnchorType, anchorId: string, body: string, onSuccess?: () => void) {
    const user = this.usersService.currentUser$.value
    if (user === null) {
      return
    }
    this.editorThreadsService
      .postComment({
        callId: this.call.id,
        anchorType,
        anchorId,
        body,
        currentUser: { id: user.id, name: constructNameForUser(user) },
      })
      .then(() => onSuccess?.())
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : 'unknown error'
        this.logger.warn(`Failed to post ${anchorType} comment: ${message}`)
      })
  }

  addComment() {
    if (this.editor === null || !this.canUserComment) {
      return
    }
    const { from, to } = this.editor.state.selection
    if (from === to) {
      return
    }
    const commentAnchorType = this.editor.state.schema.marks[COMMENT_ANCHOR_MARK_NAME] ?? null
    if (commentAnchorType === null) {
      return
    }
    const anchorId = crypto.randomUUID()
    this.editor.chain().setMark(COMMENT_ANCHOR_MARK_NAME, { anchorId }).setTextSelection(to).run()
    this.activeCommentDraftId$.next(anchorId)
    this.refreshCommentAnchors()
    this.editorSettingsService.setIsEditorMaximized(true)
    this.sidebarFilter$.next('comments')
  }

  onCommentSubmit(anchorId: string, body: string) {
    if (!this.canUserComment) {
      return
    }
    this.postThreadComment('TEXT_RANGE', anchorId, body, () => {
      if (this.activeCommentDraftId$.value === anchorId) {
        this.activeCommentDraftId$.next(null)
      }
    })
  }

  resolveCommentThread(anchorId: string) {
    if (!this.canUserComment || this.isCommentAnchorLocked(anchorId)) {
      return
    }
    this.editorThreadsService
      .archiveThread(this.call.id, 'TEXT_RANGE', anchorId)
      .then(() => {
        this.removeCommentAnchorMark(anchorId)
        this.refreshCommentAnchors()
        if (this.activeCommentDraftId$.value === anchorId) {
          this.activeCommentDraftId$.next(null)
        }
        this.activeCommentPopup$.next(null)
      })
      .catch((error) => {
        this.logger.error('Failed to archive comment thread', error)
      })
  }

  discardCommentDraft(anchorId: string) {
    this.removeCommentAnchorMark(anchorId)
    this.refreshCommentAnchors()
    if (this.activeCommentDraftId$.value === anchorId) {
      this.activeCommentDraftId$.next(null)
    }
    this.activeCommentPopup$.next(null)
  }

  /**
   * Whether the anchor sits in an agenda item a regeneration is rewriting.
   *
   * Its mark cannot be removed while the item is locked, so archiving the thread would leave the document
   * anchoring a comment nobody can reach.
   */
  private isCommentAnchorLocked(anchorId: string): boolean {
    if (this.editor === null) {
      return false
    }
    const range = collectCommentAnchors(this.editor.state).get(anchorId) ?? null
    return range !== null && overlapsLockedRange(this.editor.state, range.from, range.to)
  }

  private removeCommentAnchorMark(anchorId: string) {
    if (this.editor === null) {
      return
    }
    const markType = this.editor.state.schema.marks[COMMENT_ANCHOR_MARK_NAME] ?? null
    if (markType === null) {
      return
    }
    const range = collectCommentAnchors(this.editor.state).get(anchorId) ?? null
    if (range === null) {
      return
    }
    const transaction = this.editor.state.tr
    transaction.removeMark(range.from, range.to, markType.create({ anchorId }))
    transaction.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
    this.editor.view.dispatch(transaction)
  }

  viewCommentAnchor(anchorId: string) {
    if (this.editor === null) {
      return
    }
    const range = collectCommentAnchors(this.editor.state).get(anchorId) ?? null
    if (range === null) {
      return
    }
    this.moveSelectionTo(range.from)
    this.centerEditorOnPosition(range.from)
  }

  onViewThread(anchorId: string) {
    this.activeCommentPopup$.next(null)
    this.activeCommentAnchorId$.next(anchorId)
    this.sidebarFilter$.next('comments')
    this.editorSettingsService.setIsEditorMaximized(true)
  }

  setSidebarFilter(filter: SidebarFilter) {
    this.sidebarFilter$.next(filter)
  }

  private refreshCommentAnchors() {
    if (this.editor === null) {
      return
    }
    // Viewers never surface text-range comments: the marks stay in the shared
    // document but produce no anchors, so no cards, navigation, or popovers appear.
    // Quoted text is only read for the sidebar cards, so skip it when not maximized.
    const next = this.canUserComment ? collectCommentAnchorSummaries(this.editor.state, this.editorSettingsService.isEditorMaximized) : []
    if (!commentAnchorSummariesEqual(this.commentAnchors$.value, next)) {
      this.commentAnchors$.next(next)
    }
    // Drop the draft flag if its mark was removed by an external edit.
    const draftId = this.activeCommentDraftId$.value
    if (draftId !== null && !next.some((anchor) => anchor.anchorId === draftId)) {
      this.activeCommentDraftId$.next(null)
    }
  }

  private resolveSuggestion(suggestionId: string, action: 'accept' | 'reject') {
    if (this.editor === null) {
      return
    }

    const suggestionMarkType = this.editor.state.schema.marks[SUGGESTION_MARK_NAME] ?? null
    if (suggestionMarkType === null) {
      return
    }

    // A locked agenda item rejects the transaction below, so resolving would archive the thread and leave the
    // suggestion in the document with nothing left to act on it.
    if (coversLockedRange(this.editor.state, findAllRangesForSuggestion(this.editor.state, suggestionId))) {
      return
    }

    if (action === 'reject') {
      this.invertListChangeSuggestions(suggestionId)
    }

    const ranges = findAllRangesForSuggestion(this.editor.state, suggestionId)
    if (ranges.length === 0) {
      return
    }

    const transaction = this.editor.state.tr
    const sortedRanges = [...ranges].sort((rangeA, rangeB) => rangeB.from - rangeA.from)
    const schema = this.editor.state.schema

    for (const range of sortedRanges) {
      // A single suggestionId may cover insertion, deletion, and formatting ranges;
      // each range is resolved against its own type, not the suggestion's first range.
      if (range.type === SUGGESTION_TYPE_FORMATTING) {
        if (action === 'reject' && range.formattingChanges !== null) {
          // Undo the tracked formatting change before dropping the suggestion mark.
          for (const markName of range.formattingChanges.added) {
            const markType = schema.marks[markName] ?? null
            if (markType !== null) {
              transaction.removeMark(range.from, range.to, markType)
            }
          }
          for (const markName of range.formattingChanges.removed) {
            const markType = schema.marks[markName] ?? null
            if (markType !== null) {
              transaction.addMark(range.from, range.to, markType.create())
            }
          }
          const colorChange = range.formattingChanges.colorChange
          if (colorChange !== undefined) {
            const textStyleType = schema.marks[TEXT_STYLE_MARK_NAME] ?? null
            if (textStyleType !== null) {
              transaction.removeMark(range.from, range.to, textStyleType)
              if (colorChange.from !== null) {
                transaction.addMark(range.from, range.to, textStyleType.create({ [COLOR_ATTRIBUTE_NAME]: colorChange.from }))
              }
            }
          }
        }
        transaction.removeMark(range.from, range.to, suggestionMarkType)
        continue
      }

      const removeMark = (action === 'accept' && range.type === SUGGESTION_TYPE_INSERTION) || (action === 'reject' && range.type === SUGGESTION_TYPE_DELETION)
      if (removeMark) {
        transaction.removeMark(range.from, range.to, suggestionMarkType)
      } else {
        const deletion = range.type === SUGGESTION_TYPE_DELETION ? expandDeletionToEmptiedListItem(transaction.doc, range) : range
        transaction.delete(deletion.from, deletion.to)
      }
    }

    transaction.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
    this.editor.view.dispatch(transaction)
    this.refreshActiveSuggestion(null)

    this.editorThreadsService.archiveThread(this.call.id, 'SUGGESTION', suggestionId).catch((error) => {
      this.logger.error('Failed to archive suggestion thread', error)
    })
  }

  // Inverts every listChange entry on the suggestion's formatting ranges, applied
  // in reverse order so the most recent change is rolled back first. Each inversion
  // dispatches its own transaction with TRACK_CHANGES_SKIP_META; positions are
  // re-resolved before each step in case prior inversions shifted the range.
  // The iteration cap is a safety net — each step is supposed to drop one entry,
  // so we never need more iterations than the largest possible listChanges length
  // we could see. A standalone count keeps an unexpected no-op step from looping.
  private static readonly LIST_INVERSION_ITERATION_CAP = 64

  private invertListChangeSuggestions(suggestionId: string) {
    if (this.editor === null) {
      return
    }

    for (let iteration = 0; iteration < TiptapEditorComponent.LIST_INVERSION_ITERATION_CAP; iteration++) {
      const target = this.findNextListChangeInversionTarget(suggestionId)
      if (target === null) {
        return
      }
      this.dispatchStructuralInversion(suggestionId, target.rangeFrom, target.listChange)
      this.dispatchListChangesRewrite(suggestionId, target.remainingListChanges)
    }
  }

  // Returns the union range of all formatting suggestion ranges (with non-empty
  // listChanges) for the given suggestionId, plus the most-recent listChange entry
  // and the remaining ones.
  private findNextListChangeInversionTarget(
    suggestionId: string
  ): { rangeFrom: number; rangeTo: number; listChange: ListChange; remainingListChanges: ListChange[] } | null {
    if (this.editor === null) {
      return null
    }
    const ranges = findAllRangesForSuggestion(this.editor.state, suggestionId)
    let rangeFrom = Number.POSITIVE_INFINITY
    let rangeTo = Number.NEGATIVE_INFINITY
    let listChanges: ListChange[] | null = null
    for (const range of ranges) {
      if (range.type !== SUGGESTION_TYPE_FORMATTING) {
        continue
      }
      const candidates = range.formattingChanges?.listChanges ?? []
      if (candidates.length === 0) {
        continue
      }
      // Every formatting range under one suggestionId carries the same listChanges
      // array, so we read it once and span our selection across all ranges so
      // multi-item lists get inverted in one go.
      listChanges = listChanges ?? candidates
      if (range.from < rangeFrom) {
        rangeFrom = range.from
      }
      if (range.to > rangeTo) {
        rangeTo = range.to
      }
    }
    if (listChanges === null) {
      return null
    }
    const lastIndex = listChanges.length - 1
    return {
      rangeFrom,
      rangeTo,
      listChange: listChanges[lastIndex],
      remainingListChanges: listChanges.slice(0, lastIndex),
    }
  }

  private dispatchStructuralInversion(suggestionId: string, rangeFrom: number, listChange: ListChange) {
    if (listChange.kind === 'wrap') {
      // `wrap` introduced both a list and a listItem; lift twice in one chain.
      this.dispatchInversionChain(rangeFrom, (chain) => chain.command(liftListItemSingleLevel).command(liftListItemSingleLevel))
      return
    }
    if (listChange.kind === 'unwrap') {
      this.dispatchInversionChain(rangeFrom, (chain) => chain.wrapInList(listChange.listType))
      return
    }
    if (listChange.kind === 'changeListType') {
      this.dispatchInversionChain(rangeFrom, (chain) =>
        chain.command(({ state, tr, dispatch }) => {
          const targetType = state.schema.nodes[listChange.from] ?? null
          if (targetType === null) {
            return false
          }
          return swapListType(state, dispatch, tr, listChange.to, targetType)
        })
      )
      return
    }
    if (listChange.source !== undefined) {
      const source = listChange.source
      this.dispatchInversionChain(rangeFrom, (chain) => chain.command(restoreListItemAtSource(source)))
      return
    }
    // Older suggestions captured before sourcePosition was recorded fall back to
    // per-level steps. Mirror the original direction: positive delta (indent
    // applied via sink) inverts with lift; negative delta (outdent applied via
    // lift) inverts with sink. Each step may move the item, so re-resolve the range.
    const count = Math.abs(listChange.delta)
    const invertStep: (chain: ChainedCommands) => ChainedCommands =
      listChange.delta > 0 ? (chain) => chain.command(liftListItemSingleLevel) : (chain) => chain.sinkListItem(LIST_ITEM_NODE_NAME)
    for (let index = 0; index < count; index++) {
      const range = this.firstFormattingRangeForSuggestion(suggestionId)
      if (range === null) {
        return
      }
      const dispatched = this.dispatchInversionChain(range.from, invertStep)
      if (!dispatched) {
        return
      }
    }
  }

  private dispatchInversionChain(rangeFrom: number, applyChain: (chain: ChainedCommands) => ChainedCommands): boolean {
    if (this.editor === null) {
      return false
    }
    // Place a cursor at the start of the range. PM list commands consult the selection
    // to find the listItem ancestor; a cursor produces a single, well-defined block
    // range, while a wide selection can confuse `liftListItem` into lifting too far.
    const chain = this.editor.chain().setTextSelection(rangeFrom)
    return applyChain(chain)
      .command(({ tr }) => {
        tr.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
        return true
      })
      .run()
  }

  private firstFormattingRangeForSuggestion(suggestionId: string): { from: number; to: number } | null {
    if (this.editor === null) {
      return null
    }
    let from = Number.POSITIVE_INFINITY
    let to = Number.NEGATIVE_INFINITY
    let found = false
    for (const range of findAllRangesForSuggestion(this.editor.state, suggestionId)) {
      if (range.type !== SUGGESTION_TYPE_FORMATTING) {
        continue
      }
      found = true
      if (range.from < from) {
        from = range.from
      }
      if (range.to > to) {
        to = range.to
      }
    }
    return found ? { from, to } : null
  }

  private dispatchListChangesRewrite(suggestionId: string, remaining: ListChange[]) {
    if (this.editor === null) {
      return
    }
    const transaction = this.editor.state.tr
    this.rewriteSuggestionListChanges(transaction, suggestionId, remaining)
    if (transaction.steps.length === 0) {
      return
    }
    transaction.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
    this.editor.view.dispatch(transaction)
  }

  // Replace the suggestion's formattingChanges.listChanges with `remaining`. When
  // `remaining` is empty AND added/removed are empty, drop the mark entirely so the
  // outer mark-removal pass has nothing to do here.
  private rewriteSuggestionListChanges(tr: Transaction, suggestionId: string, remaining: ListChange[]) {
    if (this.editor === null) {
      return
    }
    const suggestionMarkType = this.editor.state.schema.marks[SUGGESTION_MARK_NAME] ?? null
    if (suggestionMarkType === null) {
      return
    }

    // Touch every text node carrying the mark — sibling nodes that differ in
    // their other inline marks each hold their own suggestion-mark instance,
    // and skipping any of them would leave stale listChanges behind for the
    // next iteration of the inversion loop to find.
    const updates: { from: number; to: number; oldMark: ProseMirrorMark; nextAttrs: Record<string, unknown> | null }[] = []

    tr.doc.descendants((node, pos) => {
      if (!node.isText) {
        return true
      }
      const mark =
        node.marks.find(
          (candidate) =>
            candidate.type.name === SUGGESTION_MARK_NAME &&
            candidate.attrs['suggestionId'] === suggestionId &&
            candidate.attrs['type'] === SUGGESTION_TYPE_FORMATTING
        ) ?? null
      if (mark === null) {
        return false
      }

      const formattingChanges = (mark.attrs['formattingChanges'] as FormattingChanges | null) ?? { added: [], removed: [] }
      const nextFormatting: FormattingChanges = { added: formattingChanges.added, removed: formattingChanges.removed }
      if (formattingChanges.colorChange !== undefined) {
        nextFormatting.colorChange = formattingChanges.colorChange
      }
      if (remaining.length > 0) {
        nextFormatting.listChanges = remaining
      }
      const isEmpty =
        nextFormatting.added.length === 0 &&
        nextFormatting.removed.length === 0 &&
        (nextFormatting.listChanges ?? []).length === 0 &&
        nextFormatting.colorChange === undefined
      const nextAttrs = isEmpty ? null : { ...mark.attrs, formattingChanges: nextFormatting }
      updates.push({ from: pos, to: pos + node.nodeSize, oldMark: mark, nextAttrs })
      return false
    })

    for (const update of updates) {
      tr.removeMark(update.from, update.to, update.oldMark)
      if (update.nextAttrs !== null) {
        tr.addMark(update.from, update.to, suggestionMarkType.create(update.nextAttrs))
      }
    }
  }

  gotoNextChange() {
    this.gotoAdjacentChange('next')
  }

  gotoPreviousChange() {
    this.gotoAdjacentChange('previous')
  }

  viewSuggestion(suggestionId: string) {
    if (this.editor === null) {
      return
    }

    const ranges = findAllRangesForSuggestion(this.editor.state, suggestionId)
    if (ranges.length === 0) {
      return
    }
    const position = ranges[0].from
    const view = this.editor.view
    view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, position)))
    view.focus()
    this.centerEditorOnPosition(position)
  }

  openSuggestionComments(suggestionId: string) {
    const wasMaximized = this.editorSettingsService.isEditorMaximized
    this.editorSettingsService.setIsEditorMaximized(true)
    if (wasMaximized) {
      this.viewSuggestion(suggestionId)
      return
    }
    // Wait until Angular has rendered the .maximized layout and the browser has
    // laid it out — otherwise `coordsAtPos` / `getBoundingClientRect` in
    // `viewSuggestion` use stale pre-maximize coordinates and the centred scroll
    // lands in the wrong place. Two RAFs: one for change detection, one for paint.
    this.cancelOpenCommentsRafs()
    this.openCommentsRafIds[0] = requestAnimationFrame(() => {
      this.openCommentsRafIds[1] = requestAnimationFrame(() => {
        this.openCommentsRafIds = [null, null]
        if (this.editor !== null) {
          this.viewSuggestion(suggestionId)
        }
      })
    })
  }

  private cancelOpenCommentsRafs() {
    this.openCommentsRafIds.forEach((rafId) => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
    })
    this.openCommentsRafIds = [null, null]
  }

  private centerEditorOnPosition(position: number) {
    if (this.editor === null) {
      return
    }
    centerViewOnPosition(this.editor.view, position)
  }

  private gotoAdjacentChange(direction: 'next' | 'previous') {
    if (this.editor === null) {
      return
    }

    const startPositions = this.filteredNavigationPositions()
    if (startPositions.length === 0) {
      return
    }

    const currentPosition = this.editor.state.selection.from
    const target =
      direction === 'next'
        ? (startPositions.find((position) => position > currentPosition) ?? startPositions[0])
        : (startPositions.findLast((position) => position < currentPosition) ?? startPositions[startPositions.length - 1])

    this.moveSelectionTo(target)
  }

  private filteredNavigationPositions(): number[] {
    if (this.editor === null) {
      return []
    }
    const includes = navigationIncludes(this.sidebarFilter$.value)
    const changePositions = includes.changes ? collectSuggestionStartPositions(this.editor.state) : []
    const commentPositions = includes.comments ? collectCommentAnchorStartPositions(this.editor.state) : []
    return [...changePositions, ...commentPositions].sort((left, right) => left - right)
  }

  private moveSelectionTo(position: number) {
    if (this.editor === null) {
      return
    }
    const transaction = this.editor.state.tr.setSelection(TextSelection.create(this.editor.state.doc, position))
    transaction.scrollIntoView()
    this.editor.view.dispatch(transaction)
    this.editor.view.focus()
  }

  private refreshSuggestionCount() {
    if (this.editor === null) {
      return
    }
    const count = collectSuggestionStartPositions(this.editor.state).length
    if (this.suggestionCount$.value !== count) {
      this.suggestionCount$.next(count)
    }
  }

  private refreshSuggestions() {
    if (this.editor === null) {
      return
    }
    const next = this.editorSettingsService.isEditorMaximized ? collectAllSuggestions(this.editor.state) : []
    if (suggestionSummariesEqual(this.suggestions$.value, next)) {
      return
    }
    this.suggestions$.next(next)
  }

  private refreshActiveSuggestion(next: ActiveSuggestion | null) {
    const current = this.activeSuggestion$.value

    if (current === null && next === null) {
      return
    }
    if (
      current !== null &&
      next !== null &&
      current.suggestionId === next.suggestionId &&
      current.authorName === next.authorName &&
      current.createdAt === next.createdAt &&
      suggestionRunsEqual(current.insertionRuns, next.insertionRuns) &&
      suggestionRunsEqual(current.deletionRuns, next.deletionRuns) &&
      stringArraysEqual(current.formattingAdded, next.formattingAdded) &&
      stringArraysEqual(current.formattingRemoved, next.formattingRemoved) &&
      listChangeArraysEqual(current.listChanges, next.listChanges) &&
      colorChangesEqual(current.colorChange, next.colorChange)
    ) {
      return
    }

    this.activeSuggestion$.next(next)
  }

  private findCommentAnchorAtCursor(props: { state: EditorState; oldState?: EditorState; from: number; to: number }): string | null {
    if (this.editor === null || props.from !== props.to) {
      return null
    }
    const oldState = props.oldState ?? null
    if (oldState !== null && !oldState.doc.eq(props.state.doc)) {
      return null
    }
    return findCommentAnchorIdAtPosition(props.state, props.from)
  }

  private refreshActiveCommentAnchor(anchorId: string | null) {
    if (this.activeCommentAnchorId$.value !== anchorId) {
      this.activeCommentAnchorId$.next(anchorId)
    }
  }

  toggleBoldOnSelection() {
    this.editor?.chain().focus().toggleBold().run()
  }

  toggleItalicOnSelection() {
    this.editor?.chain().focus().toggleItalic().run()
  }

  toggleHighlightOnSelection() {
    this.editor?.chain().focus().toggleHighlight().run()
  }

  applyCurrentTextColor() {
    if (this.editor === null) {
      return
    }
    runColorCommandWithTracking(this.editor, this.trackChangesCommandOptions(), this.currentTextColor)
  }

  selectTextColor(color: string) {
    this.currentTextColor = color
    this.applyCurrentTextColor()
  }

  toggleBulletList() {
    this.runTrackedListCommand((chain) => chain.toggleBulletList())
  }

  toggleOrderedList() {
    this.runTrackedListCommand((chain) => chain.toggleOrderedList())
  }

  increaseIndent() {
    this.runTrackedListCommand((chain) => chain.sinkListItem(LIST_ITEM_NODE_NAME))
  }

  decreaseIndent() {
    this.runTrackedListCommand((chain) => chain.command(liftListItemSingleLevel))
  }

  private trackChangesCommandOptions() {
    return {
      getEnabled: () => this.trackChangesEnabled$.value,
      getCurrentUser: () => {
        const currentUser = this.usersService.currentUser$.value
        if (currentUser === null) {
          return null
        }
        return { id: currentUser.id, name: constructNameForUser(currentUser) }
      },
    }
  }

  private runTrackedListCommand(applyCommand: (chain: ChainedCommands) => ChainedCommands) {
    if (this.editor === null) {
      return
    }
    runListCommandWithTracking(this.editor, this.trackChangesCommandOptions(), applyCommand)
  }

  private handleOpenMeeting(event: { startSentenceId: string; endSentenceId: string }) {
    // Clear editor.max in the same navigate as the highlight params; a separate setIsEditorMaximized(false)
    // would race with this call and Angular Router would cancel one of them.
    this.editorSettingsService.updateMaximizedStateOnly(false)
    this.router.navigate([], {
      queryParams: {
        sentenceId: event.startSentenceId,
        endSentenceId: event.endSentenceId,
        highlight: true,
        [EDITOR_MAXIMIZED_QUERY_PARAM]: null,
      },
      queryParamsHandling: 'merge',
    })
    this.openMeeting.emit()
  }

  async reset() {
    if (this.editor === null) {
      return
    }

    try {
      const response = await lastValueFrom(this.http.put<{ content: object }>(`/api/tiptap-editor/document/${this.call.id}/reset`, {}))
      this.editor.commands.setContent(response.content)
    } catch (error) {
      this.toastService.show('EDITOR.RESET_FAILED')
      this.logger.error('Failed to reset editor content', error)
    }
  }

  async performAiAction(action: LlmAction) {
    if (this.editor === null) {
      return
    }

    const actionConfig = AI_ACTION_CONFIGS.find((config) => config.action === action) ?? null
    if (actionConfig === null) {
      return
    }

    try {
      expandSelectionToWordBoundaries(this.editor)
    } catch (error) {
      this.toastService.show('EDITOR.AI_ACTION_FAILED')
      this.logger.error('Failed to expand selection to word boundaries', error)
      return
    }

    const { from, to } = this.editor.state.selection
    const selectedText = getSelectedText(this.editor)

    if (selectedText === null) {
      this.toastService.show('EDITOR.AI_ACTION_FAILED')
      return
    }

    const sentenceIdPairs = getSentenceIdPairsFromSelection(this.editor, this.call.outlineItems)
    const templateItemIds = getTemplateItemIdsFromSelection(this.editor)
    const existingItemContent = getExistingItemContent(this.editor)
    const actionDisplayName = this.translateService.instant(actionConfig.i18nKey)

    this.scrollTranscriptToSentences(sentenceIdPairs)

    try {
      if (actionConfig.stream) {
        await this.handleStreamingAction(action, actionDisplayName, selectedText, sentenceIdPairs, templateItemIds, existingItemContent, from, to)
      } else {
        await this.handleSyncAction(action, actionDisplayName, selectedText, sentenceIdPairs, templateItemIds, existingItemContent, from, to)
      }
    } catch (error) {
      this.toastService.show('EDITOR.AI_ACTION_FAILED')
      this.logger.error('AI action failed', error)
    }
  }

  private async handleStreamingAction(
    action: LlmAction,
    actionDisplayName: string,
    selectedText: string,
    sentenceIdPairs: SentenceIdPair[],
    templateItemIds: number[],
    existingItemContent: string | null,
    selectionFrom: number,
    selectionTo: number
  ) {
    const dialogData: AiActionStreamingDialogData = {
      callId: this.call.id,
      action,
      actionDisplayName,
      originalContent: selectedText,
      sentenceIdPairs,
      templateItemIds,
      existingItemContent,
    }

    const dialogRef = this.dialog.open(AiActionStreamingDialogComponent, {
      data: dialogData,
      hasBackdrop: false,
      autoFocus: false,
      panelClass: 'ai-action-dialog-panel',
    })

    const result = await lastValueFrom(dialogRef.afterClosed())

    if (result !== null && result !== undefined && this.editor !== null) {
      this.metricsService.sendMetric(this.call.id, MetricType.CLICK_LLM_LIVE_ACTION_INSERTION_OF_SUGGESTION)
      this.replaceRangeBypassingTrackChanges(selectionFrom, selectionTo, result.generatedText)
    }
  }

  private async handleSyncAction(
    action: LlmAction,
    actionDisplayName: string,
    selectedText: string,
    sentenceIdPairs: SentenceIdPair[],
    templateItemIds: number[],
    existingItemContent: string | null,
    selectionFrom: number,
    selectionTo: number
  ) {
    const dialogData: AiActionFactCheckDialogData = {
      callId: this.call.id,
      action,
      actionDisplayName,
      selectionContent: selectedText,
      sentenceIdPairs,
      templateItemIds,
      existingItemContent,
      onOpenDocument: (event) => this.openDocument.emit(event),
      onOpenMeeting: (event) => this.handleOpenMeeting(event),
    }

    const dialogRef = this.dialog.open(AiActionFactCheckDialogComponent, {
      data: dialogData,
      hasBackdrop: false,
      autoFocus: false,
      panelClass: 'ai-action-dialog-panel',
    })

    const result = await lastValueFrom(dialogRef.afterClosed())

    if (result !== null && result !== undefined && this.editor !== null) {
      this.metricsService.sendMetric(this.call.id, MetricType.CLICK_LLM_LIVE_ACTION_INSERTION_OF_SUGGESTION)
      this.replaceRangeBypassingTrackChanges(selectionFrom, selectionTo, result.insertionText)
    }
  }

  private replaceRangeBypassingTrackChanges(from: number, to: number, content: string) {
    if (this.editor === null) {
      return
    }
    this.editor
      .chain()
      .focus()
      .command(({ tr }) => {
        tr.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
        return true
      })
      .insertContentAt({ from, to }, content)
      .run()
  }

  private smoothScrollToSelection(view: EditorView): boolean {
    const container = findScrollableAncestor(view.dom as HTMLElement)
    if (container === null) {
      return false
    }
    const coords = view.coordsAtPos(view.state.selection.head)
    const rect = container.getBoundingClientRect()
    const margin = 64
    let target = container.scrollTop
    if (coords.bottom > rect.bottom - margin) {
      target += coords.bottom - rect.bottom + margin
    } else if (coords.top < rect.top + margin) {
      target += coords.top - rect.top - margin
    } else {
      return true
    }
    container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' })
    return true
  }

  private scrollToTemplateItem(templateItemId: number) {
    console.debug(`[TipTapEditor] Scrolling to minute node with template item ID ${templateItemId}`)

    if (this.editor === null) {
      return
    }

    const element = this.editor.view.dom.querySelector(`[data-template-item-id="${templateItemId}"]`)

    if (element !== null) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      console.warn(`[TipTapEditor] Could not find element to scroll to with selector [data-template-item-id="${templateItemId}"]`)
    }
  }

  private scrollTranscriptToSentences(sentenceIdPairs: SentenceIdPair[]) {
    if (sentenceIdPairs.length === 0) {
      return
    }

    const startSentenceId = sentenceIdPairs[0].startSentenceId
    const endSentenceId = sentenceIdPairs[sentenceIdPairs.length - 1].endSentenceId

    this.router.navigate([], {
      queryParams: { sentenceId: startSentenceId, endSentenceId, highlight: true },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }
}
